'use client';

import { useEffect } from 'react';

const SEEN_KEY = 'loristry_subscribe_modal_seen';

/** A page opts out of the exit-intent pop-up by rendering this attribute anywhere in
 *  its markup. Read at event time, so it follows client-side navigation with no state
 *  to keep in sync. The Storyworth campaign page uses it: a paid landing page with one
 *  job, and its own pop-up for visitors heading to the main site. */
const OPT_OUT_SELECTOR = '[data-exit-intent="off"]';

/** Desktop exit-intent is the only trigger, so the callback carries no trigger name.
 *  A scroll-up trigger used to sit beside it; it fired on ordinary mobile reading
 *  (34 shows, 0 submits, week of 2026-08-31) and was removed after the Reddit replay
 *  review. Once per visitor, gated in localStorage. */
export function useSubscribeModalTrigger(onReveal: () => void) {
  useEffect(() => {
    // Pointer leaves toward the top chrome (address bar, tabs) — the visitor is leaving.
    function onMouseOut(e: MouseEvent) {
      if (e.relatedTarget || e.clientY > 0) return;
      // Before the gate: an opted-out page must not spend the visitor's one showing.
      if (document.querySelector(OPT_OUT_SELECTOR)) return;
      try {
        if (window.localStorage.getItem(SEEN_KEY) === '1') return;
        window.localStorage.setItem(SEEN_KEY, '1');
      } catch {
        /* storage unavailable (private mode): no gate, reveal anyway */
      }
      onReveal();
    }
    document.addEventListener('mouseout', onMouseOut);
    return () => document.removeEventListener('mouseout', onMouseOut);
  }, [onReveal]);
}
